import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export type ThemeName = 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'teal' | 'slate' | 'rose' | 'indigo' | 'cyan';
export type ThemeMode = 'light' | 'dark';

export interface Theme {
  name: ThemeName;
  displayName: string;
  colors: {
    primary: string;
    primaryDark: string;
    primaryLight: string;
    secondary: string;
    accent: string;
    success: string;
    warning: string;
    error: string;
    gradient: string;
  };
}

export const themes: Record<ThemeName, Theme> = {
  blue: {
    name: 'blue',
    displayName: 'Azul Sereno',
    colors: {
      primary: '#5b8db8',
      primaryDark: '#3d6a8f',
      primaryLight: '#8fb8d9',
      secondary: '#4a6d8c',
      accent: '#6ba3c7',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #5b8db8 0%, #3d6a8f 100%)',
    },
  },
  green: {
    name: 'green',
    displayName: 'Verde Cropfield',
    colors: {
      primary: '#3d8b5e',
      primaryDark: '#2d6b47',
      primaryLight: '#6bb88a',
      secondary: '#3a7055',
      accent: '#5aa87a',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #3d8b5e 0%, #2d6b47 100%)',
    },
  },
  purple: {
    name: 'purple',
    displayName: 'Lavanda Suave',
    colors: {
      primary: '#8b7bb5',
      primaryDark: '#6b5a96',
      primaryLight: '#b0a3d0',
      secondary: '#6e5e94',
      accent: '#9b8bc4',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #8b7bb5 0%, #6b5a96 100%)',
    },
  },
  orange: {
    name: 'orange',
    displayName: 'Terracota',
    colors: {
      primary: '#c48a5a',
      primaryDark: '#a06b3d',
      primaryLight: '#dba87a',
      secondary: '#8c6342',
      accent: '#b87d50',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #c48a5a 0%, #a06b3d 100%)',
    },
  },
  red: {
    name: 'red',
    displayName: 'Coral Suave',
    colors: {
      primary: '#c27070',
      primaryDark: '#a05050',
      primaryLight: '#d99a9a',
      secondary: '#8c4e4e',
      accent: '#b86868',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #c27070 0%, #a05050 100%)',
    },
  },
  teal: {
    name: 'teal',
    displayName: 'Verde-Agua',
    colors: {
      primary: '#5a9e96',
      primaryDark: '#3d7d76',
      primaryLight: '#82bfb8',
      secondary: '#4a8078',
      accent: '#6db0a8',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #5a9e96 0%, #3d7d76 100%)',
    },
  },
  slate: {
    name: 'slate',
    displayName: 'Cinza Elegante',
    colors: {
      primary: '#7a8a9a',
      primaryDark: '#566a7c',
      primaryLight: '#9eadb8',
      secondary: '#5a6a78',
      accent: '#6d7d8d',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #7a8a9a 0%, #566a7c 100%)',
    },
  },
  rose: {
    name: 'rose',
    displayName: 'Rosa Antigo',
    colors: {
      primary: '#b87088',
      primaryDark: '#965770',
      primaryLight: '#d4a0b0',
      secondary: '#7d4e60',
      accent: '#c48898',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #b87088 0%, #965770 100%)',
    },
  },
  indigo: {
    name: 'indigo',
    displayName: 'Indigo Suave',
    colors: {
      primary: '#7078b8',
      primaryDark: '#545ca0',
      primaryLight: '#9aa0d4',
      secondary: '#4e5490',
      accent: '#8088c4',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #7078b8 0%, #545ca0 100%)',
    },
  },
  cyan: {
    name: 'cyan',
    displayName: 'Azul Celeste',
    colors: {
      primary: '#5a9eb8',
      primaryDark: '#3d7d96',
      primaryLight: '#82bfd4',
      secondary: '#4a8098',
      accent: '#6db0c4',
      success: '#5da87a',
      warning: '#d4a054',
      error: '#c75c5c',
      gradient: 'linear-gradient(135deg, #5a9eb8 0%, #3d7d96 100%)',
    },
  },
};

interface ThemeContextType {
  theme: Theme;
  themeName: ThemeName;
  themeMode: ThemeMode;
  setTheme: (themeName: ThemeName) => Promise<void>;
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  toggleThemeMode: () => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [themeName, setThemeName] = useState<ThemeName>('green');
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserTheme = async () => {
      if (user?.dbId) {
        try {
          const { data, error } = await supabase
            .from('usuarios')
            .select('theme_preference')
            .eq('id', user.dbId)
            .maybeSingle();

          if (error) {
            console.error('Error loading theme:', error);
          } else if (data?.theme_preference) {
            const prefs = data.theme_preference as { color?: ThemeName; mode?: ThemeMode };
            if (prefs.color) setThemeName(prefs.color);
            if (prefs.mode) setThemeModeState(prefs.mode);
          }
        } catch (error) {
          console.error('Error loading theme:', error);
        }
      }
      setIsLoading(false);
    };

    loadUserTheme();
  }, [user?.dbId]);

  useEffect(() => {
    const currentTheme = themes[themeName];
    const root = document.documentElement;

    root.style.setProperty('--color-primary', currentTheme.colors.primary);
    root.style.setProperty('--color-primary-dark', currentTheme.colors.primaryDark);
    root.style.setProperty('--color-primary-light', currentTheme.colors.primaryLight);
    root.style.setProperty('--color-primary-container', currentTheme.colors.primaryDark);
    root.style.setProperty('--color-primary-fixed', currentTheme.colors.primaryLight);
    root.style.setProperty('--color-tertiary', currentTheme.colors.primary);
    root.style.setProperty('--color-tertiary-container', currentTheme.colors.primaryDark);
    root.style.setProperty('--color-secondary', currentTheme.colors.secondary);
    root.style.setProperty('--color-accent', currentTheme.colors.accent);
    root.style.setProperty('--color-success', currentTheme.colors.success);
    root.style.setProperty('--color-warning', currentTheme.colors.warning);
    root.style.setProperty('--color-error', currentTheme.colors.error);

    if (themeMode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [themeName, themeMode]);

  const setTheme = async (newThemeName: ThemeName) => {
    setThemeName(newThemeName);

    if (!user?.dbId) return;

    try {
      const { error } = await supabase
        .from('usuarios')
        .update({
          theme_preference: { color: newThemeName, mode: themeMode }
        })
        .eq('id', user.dbId);

      if (error) {
        console.error('Error saving theme:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error setting theme:', error);
      throw error;
    }
  };

  const setThemeMode = async (mode: ThemeMode) => {
    setThemeModeState(mode);

    if (!user?.dbId) return;

    try {
      const { error } = await supabase
        .from('usuarios')
        .update({
          theme_preference: { color: themeName, mode }
        })
        .eq('id', user.dbId);

      if (error) {
        console.error('Error saving theme mode:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error setting theme mode:', error);
      throw error;
    }
  };

  const toggleThemeMode = async () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    await setThemeMode(newMode);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme: themes[themeName],
        themeName,
        themeMode,
        setTheme,
        setThemeMode,
        toggleThemeMode,
        isLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
